import React, { useContext, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link, useHistory, useRouteMatch, useLocation } from 'react-router-dom'
import { IconName, IconProp } from '@fortawesome/fontawesome-svg-core'
import 'react-datepicker/dist/react-datepicker.css'
import { Button } from '../../components/shadcn/Button'
import { OrderDetailContext, useOrderDetailContext } from './OrderDetailContext'
import { Tag } from '../../components/Tag'
import { useOrderFilters } from '../../lib/hooks/useOrderFilters'
import { graphql, useFragment } from '../../gql'
import { faArrowLeft, faMoneyBill, faFragile, faExpandAlt, faNote } from '@fortawesome/pro-solid-svg-icons'
import { ProfileDropdown } from '../../components/ProfileDropdown'

const OrderHeader_OrderFragment = graphql(`
  fragment OrderHeader_OrderFragment on Order {
    id
    reservationNumber
    isPaid
    processStatus {
      name
    }
    files {
      id
    }
    shippingAddress {
      firstName
      lastName
    }
    note
    privateNote
    tags {
      id
    }
    items {
      id
      product {
        isFragile
        isOversize
      }
    }
    tags {
      ...TagComponent_TagFragment
    }
  }
`)

export const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [searchIcon, setSearchIcon] = useState<IconName>('search')
  const history = useHistory()
  const { path } = useRouteMatch()
  const location = useLocation()
  const { fullDashboardUrl } = useOrderFilters()
  const searchParams = new URLSearchParams(location.search)
  const group = searchParams.get('group')

  const orderContext = useOrderDetailContext()
  const order = useFragment(OrderHeader_OrderFragment, orderContext.order)

  const hasFragiles = order?.items?.filter(oi => oi.product?.isFragile).length! > 0
  const hasOversized = order?.items?.filter(oi => oi.product?.isOversize).length! > 0

  const menuItems = useMemo(
    () => [
      {
        label: 'Položky',
        icon: ['far', 'list-check'],
        link: `/order/${order?.id}/items` + (group ? `?group=${group}` : ''),
        pathPattern: /\/items$/,
        badgeCount: 0,
      },
      {
        label: 'Informace',
        icon: ['far', 'info-circle'],
        link: `/order/${order?.id}/info` + (group ? `?group=${group}` : ''),
        pathPattern: /\/info$/,
        badgeCount: 0,
      },
      {
        label: 'Soubory',
        icon: ['far', 'folders'],
        link: `/order/${order?.id}/files` + (group ? `?group=${group}` : ''),
        pathPattern: /\/files$/,
        badgeCount: order?.files?.length || 0,
      },
    ],
    [order?.id],
  )

  // Determine the initial tab based on current URL
  const getInitialTab = () => {
    const currentPath = location.pathname
    const tabIndex = menuItems.findIndex(item => item.pathPattern.test(currentPath))
    return tabIndex !== -1 ? tabIndex + 1 : 1
  }

  const [openTab, setOpenTab] = useState<number>(getInitialTab())

  // Update selected tab when URL changes
  React.useEffect(() => {
    const newTab = getInitialTab()
    setOpenTab(newTab)
  }, [location.pathname])

  return (
    <>
      <nav className="bg-white border-b border-cool-grey flex justify-between items-center min-h-16 px-3">
        <div className="flex flex-row divide-x divide-cool-grey mr-4 items-center">
          <Button
            onClick={() => (group ? history.push(`/group/${group}`) : history.push(`${fullDashboardUrl}`))}
            variant="outline"
            size="md"
            className={'mr-6'}>
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Zpět
          </Button>
          <div>
            <div className="font-bold text-xl px-6">
              Obj. #{order?.reservationNumber}
              {order?.shippingAddress
                ? ` — ${order?.shippingAddress?.firstName} ${order?.shippingAddress?.lastName}`
                : ''}
            </div>
            <div className="text-sm text-primary-blue font-semibold px-6">{order?.processStatus?.name}</div>
          </div>
        </div>
        <div className={'flex gap-8 items-center py-2.5'}>
          <div className={'flex items-center gap-3 flex-wrap'}>
            {!order?.isPaid && (
              <div>
                <span className={'label-red'}>
                  <FontAwesomeIcon icon={faMoneyBill} size="lg" className={'mr-2'} />
                  <span>Neuhrazeno</span>
                </span>
              </div>
            )}
            {hasFragiles && (
              <div>
                <span className={'label-yellow'}>
                  <FontAwesomeIcon icon={faFragile} size="lg" className={'mr-2'} />
                  <span>Křehké</span>
                </span>
              </div>
            )}
            {hasOversized && (
              <div>
                <span className={'label-purple'}>
                  <FontAwesomeIcon icon={faExpandAlt} size="lg" className={'mr-2'} />
                  <span>Nadměrné</span>
                </span>
              </div>
            )}
            {(order?.tags || []).map(tag => (
              <div key={tag?.id}>
                <Tag tag={tag} />
              </div>
            ))}
          </div>

          <ul className="flex items-center gap-2" role="tablist">
            {menuItems.map((el, i) => (
              <li className="text-center" key={el.label}>
                <Link
                  to={el.link}
                  className={
                    'text-sm font-semibold px-6 py-3 rounded-xl flex align-center gap-3 flex-row flex-wrap justify-center ' +
                    (openTab === i + 1 ? 'bg-secondary-blue text-primary-blue' : 'bg-white text-black')
                  }
                  onClick={() => {
                    setOpenTab(i + 1)
                  }}
                  data-toggle="tab"
                  role="tablist">
                  <div className="relative">
                    <FontAwesomeIcon size={'xl'} icon={el.icon as IconProp} className="text-primary-blue self-center" />
                    {el.badgeCount > 0 && (
                      <div className="absolute -top-2 -right-3 bg-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {el.badgeCount}
                      </div>
                    )}
                  </div>
                  <span>{el.label}</span>
                </Link>
              </li>
            ))}
          </ul>
          <ProfileDropdown />
        </div>
      </nav>
      {(order?.note || order?.privateNote) && (
        <p className="text-lg font-bold px-6 py-2 bg-orange-light gap-4 flex flex-row">
          {order?.note && (
            <span className="flex-1">
              <FontAwesomeIcon icon={faNote} size="lg" className={'mr-2'} />
              {order?.note}
            </span>
          )}
          {order.privateNote && (
            <span className="text-red flex-1">
              <FontAwesomeIcon icon={faNote} size="lg" className={'mr-2'} />
              {order.privateNote}
            </span>
          )}
        </p>
      )}
    </>
  )
}
